/**
 * Session timeframes (1D / 1W) for the multi-timeframe wrapper: a daily or
 * weekly indicator value drawn on an intraday chart.
 *
 * Clock flooring (htfProjection) cannot build these: a daily bucket floored in
 * UTC starts at 19:00/20:00 New York time, not at the session open, and an
 * intraday chart never holds the year of history a 200-day line needs. So the
 * rows come from a FETCHED daily series (see htfSeriesStore), extended with
 * rows built from the chart's own regular-hours bars for the dates the fetch
 * does not cover yet, today included. Today's row therefore develops as bars
 * arrive, the Phase A "developing value" rule.
 *
 * Which row a chart bar reads (US equities, America/New_York):
 *  - A bar belongs to session D when the part of it that has happened reaches
 *    past D's 09:30 open. Premarket bars read the PREVIOUS session's final
 *    value, so the line steps at the open; regular and after-hours bars read D.
 *  - The lookup is "latest row dated on or before the bar's session", which
 *    covers weekends, holidays and halts with no special case.
 *  - Daily rows are regular-hours bars: early closes (13:00) end a session's
 *    contribution early, via `regularCloseMinutesFor`.
 *
 * Every bar mapped to one row shares that row's result object (the HtfPoint
 * reference-sharing precedent), so a row is one flat step on the chart.
 */

import type { KLineData } from 'klinecharts'
import { REGULAR_OPEN_MINUTES, regularCloseMinutesFor, sessionClock } from './sessionFilter'
import { DEFAULT_SESSION_TZ } from './sessionDay'

/** Session timeframes the wrapper builds from a daily series. */
export type SessionTimeframe = '1D' | '1W'

export function isSessionTimeframe(timeframe: unknown): timeframe is SessionTimeframe {
  return timeframe === '1D' || timeframe === '1W'
}

/** One daily (or weekly) bar, keyed by its exchange date 'YYYY-MM-DD'. */
export interface SessionRow {
  date: string
  /** 12:00 UTC on `date`, the label the API gives every non-intraday bar. */
  timestamp: number
  open: number
  high: number
  low: number
  close: number
}

/** Calc shape the projection runs (the clone-calc contract, as in mtfCalc). */
type RowCalc = (
  dataList: KLineData[],
  indicator: { calcParams?: number[]; extendData?: unknown },
) => Record<string, number>[]

/** Shared row for bars no session value applies to (warm-up, unmapped). */
const EMPTY_ROW: Record<string, number> = Object.freeze({})

export interface SessionProjectionInput {
  /** Fetched daily rows, ascending by date. */
  fetched: readonly SessionRow[]
  /** The chart's bar interval. */
  chartMs: number
  /** The chart's present (replay-aware): decides how much of the newest bar
   *  has happened, and which session is still developing. */
  nowMs: number
}

/**
 * A chart bar's exchange date, and whether it reads the session BEFORE that
 * date: true when the part of the bar that has happened (capped by `nowMs`)
 * ends at or before the 09:30 open, i.e. premarket. A forming 09:00 hour bar
 * flips to its own date only once the clock passes 09:30.
 */
function barSession(
  timestamp: number,
  chartMs: number,
  nowMs: number,
): { date: string; beforeOpen: boolean } {
  const { minutes, dateStr } = sessionClock(timestamp, DEFAULT_SESSION_TZ)
  const elapsedMs = Math.max(0, Math.min(chartMs, nowMs - timestamp))
  const reachedMinutes = minutes + elapsedMs / 60_000
  return { date: dateStr, beforeOpen: !(reachedMinutes > REGULAR_OPEN_MINUTES) }
}

/**
 * Session rows from fetched daily bars. Daily bars carry the 12:00 UTC label
 * (daily_tail.py `daily_bar_label_ts`), so the UTC date IS the session date.
 */
export function dailyRowsFromBars(
  bars: readonly { timestamp: number; open: number; high: number; low: number; close: number }[],
): SessionRow[] {
  const rows: SessionRow[] = []
  for (const b of bars) {
    if (![b.open, b.high, b.low, b.close].every(Number.isFinite)) continue
    rows.push({
      date: new Date(b.timestamp).toISOString().slice(0, 10),
      timestamp: b.timestamp,
      open: b.open,
      high: b.high,
      low: b.low,
      close: b.close,
    })
  }
  return rows
}

/** Exchange date of an instant. Floored to the minute first: `sessionClock`
 *  memoizes per timestamp, and a raw `Date.now()` per tick would fill it. */
export function sessionDateOf(ms: number): string {
  return sessionClock(Math.floor(ms / 60_000) * 60_000, DEFAULT_SESSION_TZ).dateStr
}

/** 12:00 UTC on a 'YYYY-MM-DD' date, the API's label for non-intraday bars. */
function labelOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number)
  return Date.UTC(y, m - 1, d, 12)
}

/**
 * The daily rows a projection runs over.
 *
 * Fetched rows count only when dated BEFORE the developing session (the
 * exchange date of `nowMs`). That keeps the API's forming-day tail row from
 * ever beating the chart's own live bars, and on a replay it keeps every row
 * past the replay clock out of the math.
 *
 * Every later date the chart's bars cover gets a row built from that date's
 * regular-hours bars: today (the developing value), and a yesterday the daily
 * table has not loaded yet. With no fetched row at all there is nothing to
 * extend, and nothing is built: a line from intraday bars alone would pass a
 * few weeks of chart history off as a 200-day value.
 */
function assembleRows(bars: readonly KLineData[], input: SessionProjectionInput): SessionRow[] {
  const cut = sessionDateOf(input.nowMs)
  const rows = input.fetched.filter((r) => r.date < cut)
  if (rows.length === 0) return rows
  const lastFetched = rows[rows.length - 1].date

  let building: SessionRow | null = null
  for (const b of bars) {
    const { minutes, dateStr } = sessionClock(b.timestamp, DEFAULT_SESSION_TZ)
    if (dateStr <= lastFetched) continue
    // Regular hours only: the part that has happened must reach past the open,
    // and the bar must start before the (early-close aware) regular close.
    if (barSession(b.timestamp, input.chartMs, input.nowMs).beforeOpen) continue
    if (minutes >= regularCloseMinutesFor(dateStr)) continue
    if (building && building.date === dateStr) {
      if (b.high > building.high) building.high = b.high
      if (b.low < building.low) building.low = b.low
      building.close = b.close
    } else {
      building = {
        date: dateStr,
        timestamp: labelOf(dateStr),
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
      }
      rows.push(building)
    }
  }
  return rows
}

/** The Monday on or before a 'YYYY-MM-DD' date. Date-only UTC arithmetic, so
 *  no timezone can shift it (the key is already an exchange date). */
function mondayOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  const t = Date.UTC(y, m - 1, d)
  const sinceMonday = (new Date(t).getUTCDay() + 6) % 7
  return new Date(t - sinceMonday * 86_400_000).toISOString().slice(0, 10)
}

/**
 * Group daily rows into Monday-start weeks. A week starting on a holiday
 * Monday is still that Monday's week. `weekOf[i]` is daily row i's week.
 */
export function weeklyRows(daily: readonly SessionRow[]): {
  rows: SessionRow[]
  weekOf: number[]
} {
  const rows: SessionRow[] = []
  const weekOf: number[] = new Array(daily.length)
  let current: SessionRow | null = null
  for (let i = 0; i < daily.length; i++) {
    const r = daily[i]
    const monday = mondayOf(r.date)
    if (current && current.date === monday) {
      if (r.high > current.high) current.high = r.high
      if (r.low < current.low) current.low = r.low
      current.close = r.close
    } else {
      current = {
        date: monday,
        timestamp: labelOf(monday),
        open: r.open,
        high: r.high,
        low: r.low,
        close: r.close,
      }
      rows.push(current)
    }
    weekOf[i] = rows.length - 1
  }
  return { rows, weekOf }
}

function asBars(rows: readonly SessionRow[]): KLineData[] {
  return rows.map(
    (r) =>
      ({
        timestamp: r.timestamp,
        open: r.open,
        high: r.high,
        low: r.low,
        close: r.close,
        volume: 0,
      }) as KLineData,
  )
}

/**
 * How many daily (1D) or weekly (1W) rows a projection would run over: the
 * history an indicator's period has to fit in. Drives the drawer's shortfall
 * note (base plans keep about six months of daily bars).
 */
export function sessionRowCount(
  timeframe: SessionTimeframe,
  dataList: readonly KLineData[],
  input: SessionProjectionInput,
): number {
  const rows = assembleRows(dataList, input)
  return timeframe === '1W' ? weeklyRows(rows).rows.length : rows.length
}

/**
 * 1W on a DAILY chart: the chart's own bars are the daily rows, so no fetch is
 * involved. Each bar reads its week; the newest week develops.
 */
export function projectChartWeeks(
  baseCalc: RowCalc,
  dataList: KLineData[],
  indicator: { calcParams?: number[]; extendData?: unknown },
): Record<string, number>[] {
  // Daily bars carry the 12:00 UTC label, so the UTC date IS the session date.
  const daily = dataList.map((b) => ({
    date: new Date(b.timestamp).toISOString().slice(0, 10),
    timestamp: b.timestamp,
    open: b.open,
    high: b.high,
    low: b.low,
    close: b.close,
  }))
  const { rows, weekOf } = weeklyRows(daily)
  const calcRows = baseCalc(asBars(rows), indicator)
  return weekOf.map((w) => calcRows[w] ?? EMPTY_ROW)
}

/**
 * Run `baseCalc` over session rows and project each row's value onto the
 * chart bars that read it.
 */
export function projectSessionTimeframe(
  baseCalc: RowCalc,
  dataList: KLineData[],
  indicator: { calcParams?: number[]; extendData?: unknown },
  timeframe: SessionTimeframe,
  input: SessionProjectionInput,
): Record<string, number>[] {
  const rows = assembleRows(dataList, input)
  // Bars always resolve to a DAILY row (that is where the premarket rule
  // lives); a weekly projection then reads that row's week.
  const weekly = timeframe === '1W' ? weeklyRows(rows) : null
  const calcRows = baseCalc(asBars(weekly ? weekly.rows : rows), indicator)
  const values = weekly ? weekly.weekOf.map((w) => calcRows[w]) : calcRows

  const out: Record<string, number>[] = new Array(dataList.length)
  // Bars ascend, so their sessions never go backwards: one forward pointer.
  let j = -1
  for (let i = 0; i < dataList.length; i++) {
    const { date, beforeOpen } = barSession(dataList[i].timestamp, input.chartMs, input.nowMs)
    while (
      j + 1 < rows.length &&
      (beforeOpen ? rows[j + 1].date < date : rows[j + 1].date <= date)
    ) {
      j++
    }
    out[i] = j >= 0 ? (values[j] ?? EMPTY_ROW) : EMPTY_ROW
  }
  return out
}
